import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Button, CircularProgress, Typography, Box } from '@mui/material';
import ActionConfirmModal from './ActionConfirmModal';
import { useCurrency } from '../../../utils/currency';

export default function InventoryItemModals({
    // Create Item
    inventoryItemModalOpen, setInventoryItemModalOpen, isSubmittingInventoryItem, inventoryItemForm, setInventoryItemForm, handleCreateInventoryItem,
    // Edit Item
    editInventoryItemModalOpen, setEditInventoryItemModalOpen, isUpdatingInventoryItem, editInventoryItemForm, setEditInventoryItemForm, handleUpdateInventoryItem,
    // Delete Item
    deleteInventoryItemModalOpen, setDeleteInventoryItemModalOpen, isDeletingInventoryItem, handleDeleteInventoryItem,
    adjustStockModalOpen, setAdjustStockModalOpen, isAdjustingStock, adjustStockForm, setAdjustStockForm, handleAdjustStock,
    selectedAdjustStockItem = null,
    suppliers = []
}) {
    const { currencySymbol } = useCurrency();

    const currentStock = selectedAdjustStockItem
        ? (selectedAdjustStockItem.currentStock !== undefined
            ? Number(selectedAdjustStockItem.currentStock)
            : Number(selectedAdjustStockItem.stockQuantity || selectedAdjustStockItem.stock || 0))
        : null;

    const adjustmentNum = Number(adjustStockForm.quantity);
    const hasQuantity = adjustStockForm.quantity !== '' && !isNaN(adjustmentNum);
    const newProjectedStock = currentStock !== null && hasQuantity ? currentStock + adjustmentNum : null;
    const isBelowZero = newProjectedStock !== null && newProjectedStock < 0;
    return (
        <>
            {/* Create Inventory Item Modal */}
            <Dialog 
                open={inventoryItemModalOpen} 
                onClose={() => !isSubmittingInventoryItem && setInventoryItemModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Create New Inventory Item</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="Item Name"
                                value={inventoryItemForm.name}
                                onChange={(e) => setInventoryItemForm({ ...inventoryItemForm, name: e.target.value })}
                                disabled={isSubmittingInventoryItem}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required disabled={isSubmittingInventoryItem}>
                                <InputLabel id="inventory-category-label">Category</InputLabel>
                                <Select
                                    labelId="inventory-category-label"
                                    label="Category"
                                    value={inventoryItemForm.category}
                                    onChange={(e) => setInventoryItemForm({ ...inventoryItemForm, category: e.target.value })}
                                >
                                    <MenuItem value="F&B">F&B</MenuItem>
                                    <MenuItem value="Room Supplies">Room Supplies</MenuItem>
                                    <MenuItem value="Housekeeping">Housekeeping</MenuItem>
                                    <MenuItem value="Maintenance">Maintenance</MenuItem>
                                    <MenuItem value="General">General</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required disabled={isSubmittingInventoryItem}>
                                <InputLabel id="inventory-uom-label">Unit of Measure</InputLabel>
                                <Select
                                    labelId="inventory-uom-label"
                                    label="Unit of Measure"
                                    value={inventoryItemForm.unitOfMeasure}
                                    onChange={(e) => setInventoryItemForm({ ...inventoryItemForm, unitOfMeasure: e.target.value })}
                                >
                                    <MenuItem value="piece">Piece (pc)</MenuItem>
                                    <MenuItem value="box">Box (bx)</MenuItem>
                                    <MenuItem value="pack">Pack (pk)</MenuItem>
                                    <MenuItem value="liter">Liter (L)</MenuItem>
                                    <MenuItem value="kg">Kilogram (kg)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Current Stock"
                                placeholder="0"
                                value={inventoryItemForm.currentStock}
                                onChange={(e) => setInventoryItemForm({ ...inventoryItemForm, currentStock: e.target.value })}
                                disabled={isSubmittingInventoryItem}
                                InputProps={{ inputProps: { min: 0 } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Reorder Level (Optional)"
                                placeholder="e.g. 5"
                                value={inventoryItemForm.reorderLevel}
                                onChange={(e) => setInventoryItemForm({ ...inventoryItemForm, reorderLevel: e.target.value })}
                                disabled={isSubmittingInventoryItem}
                                InputProps={{ inputProps: { min: 1 } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Maximum Stock (Optional)"
                                placeholder="e.g. 100"
                                value={inventoryItemForm.maxStock}
                                onChange={(e) => setInventoryItemForm({ ...inventoryItemForm, maxStock: e.target.value })}
                                disabled={isSubmittingInventoryItem}
                                InputProps={{ inputProps: { min: 1 } }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label={`Unit Cost (${currencySymbol}) (Optional)`}
                                placeholder="0.00"
                                value={inventoryItemForm.unitCost}
                                onChange={(e) => setInventoryItemForm({ ...inventoryItemForm, unitCost: e.target.value })}
                                disabled={isSubmittingInventoryItem}
                                InputProps={{ inputProps: { min: 0.01, step: "0.01" } }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth disabled={isSubmittingInventoryItem}>
                                <InputLabel>Preferred Supplier (Optional)</InputLabel>
                                <Select
                                    label="Preferred Supplier (Optional)"
                                    value={inventoryItemForm.supplierId}
                                    onChange={(e) => setInventoryItemForm({ ...inventoryItemForm, supplierId: e.target.value })}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {suppliers.map(supplier => (
                                         <MenuItem key={supplier.id} value={supplier.id}>
                                             {supplier.companyName || supplier.name}
                                         </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setInventoryItemModalOpen(false)} 
                        disabled={isSubmittingInventoryItem}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleCreateInventoryItem} 
                        variant="contained" 
                        disabled={
                            isSubmittingInventoryItem || 
                            !inventoryItemForm.name?.trim() || 
                            !inventoryItemForm.category || 
                            !inventoryItemForm.unitOfMeasure ||
                            (inventoryItemForm.currentStock !== '' && Number(inventoryItemForm.currentStock) < 0) ||
                            (inventoryItemForm.reorderLevel !== '' && Number(inventoryItemForm.reorderLevel) <= 0) ||
                            (inventoryItemForm.maxStock !== '' && Number(inventoryItemForm.maxStock) <= 0) ||
                            (inventoryItemForm.unitCost !== '' && Number(inventoryItemForm.unitCost) <= 0)
                        }
                        startIcon={isSubmittingInventoryItem ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isSubmittingInventoryItem ? 'Creating...' : 'Create Item'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Inventory Item Modal */}
            <Dialog 
                open={editInventoryItemModalOpen} 
                onClose={() => !isUpdatingInventoryItem && setEditInventoryItemModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Update Inventory Item</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Item Name"
                                value={editInventoryItemForm.name}
                                onChange={(e) => setEditInventoryItemForm({ ...editInventoryItemForm, name: e.target.value })}
                                disabled={isUpdatingInventoryItem}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                type="number"
                                label={`Unit Cost (${currencySymbol})`}
                                value={editInventoryItemForm.unitCost}
                                onChange={(e) => setEditInventoryItemForm({ ...editInventoryItemForm, unitCost: e.target.value })}
                                disabled={isUpdatingInventoryItem}
                                InputProps={{ inputProps: { min: 0, step: "0.01" } }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setEditInventoryItemModalOpen(false)} 
                        disabled={isUpdatingInventoryItem}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleUpdateInventoryItem} 
                        variant="contained" 
                        disabled={
                            isUpdatingInventoryItem || 
                            !editInventoryItemForm.name || 
                            editInventoryItemForm.unitCost < 0
                        }
                        startIcon={isUpdatingInventoryItem ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isUpdatingInventoryItem ? 'Updating...' : 'Update Item'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Inventory Item Confirmation Modal */}
            <ActionConfirmModal
                open={deleteInventoryItemModalOpen}
                onClose={() => setDeleteInventoryItemModalOpen(false)}
                title="Delete Inventory Item"
                message="Warning: Are you sure you want to delete this inventory item? This will permanently remove its records from the active stock ledger directory registry. This action cannot be undone."
                onConfirm={handleDeleteInventoryItem}
                isProcessing={isDeletingInventoryItem}
                confirmText="Delete Item"
                confirmColor="error"
            />

            {/* Adjust Stock Modal */}
            <Dialog 
                open={adjustStockModalOpen} 
                onClose={() => !isAdjustingStock && setAdjustStockModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Adjust Item Stock Qty</DialogTitle>
                <DialogContent dividers>
                    {selectedAdjustStockItem && (
                        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary">Item: <b>{selectedAdjustStockItem.name}</b></Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                                <Typography variant="body2">Current Stock: <b>{currentStock} {selectedAdjustStockItem.unitOfMeasure || ''}</b></Typography>
                                {hasQuantity && (
                                    <Typography variant="body2" color={isBelowZero ? 'error.main' : 'success.main'} fontWeight={600}>
                                        New Stock: {newProjectedStock} {selectedAdjustStockItem.unitOfMeasure || ''}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Adjustment Quantity (+/-)"
                                value={adjustStockForm.quantity}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    const cleaned = val === '' ? '' : val.replace(/^(-?)0+(?=[1-9])/, '$1');
                                    setAdjustStockForm({ ...adjustStockForm, quantity: cleaned });
                                }}
                                onFocus={(e) => e.target.select()}
                                disabled={isAdjustingStock}
                                error={isBelowZero}
                                helperText={
                                    isBelowZero
                                        ? `Adjusted stock quantity cannot fall below 0. Current stock is ${currentStock}.`
                                        : "Use negative numbers to reduce stock, positive to add."
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth disabled={isAdjustingStock}>
                                <InputLabel>Reason</InputLabel>
                                <Select
                                    label="Reason"
                                    value={adjustStockForm.reason}
                                    onChange={(e) => setAdjustStockForm({ ...adjustStockForm, reason: e.target.value })}
                                >
                                    <MenuItem value="Damaged during washing">Damaged during washing</MenuItem>
                                    <MenuItem value="Stock Count Discrepancy">Stock Count Discrepancy</MenuItem>
                                    <MenuItem value="Expired / Disposed">Expired / Disposed</MenuItem>
                                    <MenuItem value="Internal Consumption">Internal Consumption</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Auditing Notes"
                                value={adjustStockForm.notes}
                                onChange={(e) => setAdjustStockForm({ ...adjustStockForm, notes: e.target.value })}
                                disabled={isAdjustingStock}
                                placeholder="E.g., Voided by supervisor check"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => setAdjustStockModalOpen(false)} 
                        disabled={isAdjustingStock}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleAdjustStock} 
                        variant="contained" 
                        color="primary"
                        disabled={
                            isAdjustingStock || 
                            !adjustStockForm.reason || 
                            adjustStockForm.quantity == 0 || 
                            adjustStockForm.quantity === '' ||
                            isNaN(adjustStockForm.quantity) ||
                            isBelowZero
                        }
                        startIcon={isAdjustingStock ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isAdjustingStock ? 'Processing...' : 'Log Variance'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
